/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  updatePlanningEventField,
  fetchEventCompliances,
  fetchPlanningEvents,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { ContentComplianceStatus } from 'src/v3/core/types'
import ImageUpload from 'src/v3/components/upload-image'
import axios, { pluckData } from 'src/utilities/http-client'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import {
  downloadOriginalImage,
  getS3OriginalImageFilePath,
} from 'src/v3/utilities/image-upload'

type EventFieldProps = {
  eventID: string
  event: any // PlanningEvent type
  fieldName: keyof Pick<
    any,
    | 'name'
    | 'description'
    | 'hours'
    | 'category'
    | 'thumbnail'
    | 'banner'
    | 'quizPasscode'
    | 'virtualLocationUrl'
  >
  fieldLabel: string
  fieldType: 'text' | 'textarea' | 'number' | 'select' | 'image'
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
  options?: string[] // For select fields
  required?: boolean
  viewMode?: 'inline' | 'modal' // inline for planning, modal for compliance
  onClose?: () => void // Called when modal is closed (modal mode only)
}

let statusColors: Record<ContentComplianceStatus, any> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let statusLabels: Record<ContentComplianceStatus, string> = {
  new: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Updated - Pending Review',
  unapproved: 'Unapproved',
}

let categories = [
  'Airway|Respiratory|Ventilation',
  'Medical',
  'Trauma',
  'Operations',
  'Critical Care',
  'Cardiovascular',
  'Pediatrics',
]

export default ({
  eventID,
  event,
  fieldName,
  fieldLabel,
  fieldType,
  complianceStatus,
  complianceFeedback,
  options = [],
  required = false,
  viewMode = 'inline',
  onClose,
}: EventFieldProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)
  let [imageFile, setImageFile] = useState<File | undefined>(undefined)
  let [confirmOpen, setConfirmOpen] = useState(false)
  let [downloadingImage, setDownloadingImage] = useState(false)

  let isApproved =
    fieldName === 'hours' &&
    complianceStatus === 'approved' &&
    viewMode === 'inline'

  // TEMPORARY: Live Class thumbnail viewer - TO BE REMOVED LATER
  let [showLiveClassModal, setShowLiveClassModal] = useState(false)
  let [liveClassImageName, setLiveClassImageName] = useState<string | null>(
    null,
  )
  let [loadingLiveClassImage, setLoadingLiveClassImage] = useState(false)
  let [liveClassImageError, setLiveClassImageError] = useState<string | null>(
    null,
  )

  // Handle complex field value extraction from discriminated unions
  let currentValue = event?.[fieldName] || ''

  // Handle category field - extract string value from possible object format
  if (fieldName === 'category') {
    // First check if categoryName exists (compliance dashboard format)
    if (event?.categoryName) {
      currentValue = event.categoryName
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      // If it's an object with a 'case' property (discriminated union format)
      currentValue = (currentValue as any).case || ''
    }
    // Now currentValue is a string
  }

  // Handle quiz passcode from attendanceTrackingMechanism (both formats)
  if (fieldName === 'quizPasscode') {
    if (typeof event?.attendanceTrackingMechanism === 'object') {
      currentValue = event.attendanceTrackingMechanism?.quizPasscode || ''
    } else {
      currentValue = '' // String format doesn't contain the actual passcode
    }
  }

  // Handle virtual location URL from eventType (both formats)
  if (fieldName === 'virtualLocationUrl') {
    if (event?.eventType?.virtual) {
      currentValue = event.eventType.virtual.url || ''
    } else if ((event?.eventType as any)?.case === 'Virtual') {
      currentValue = (event.eventType as any)?.fields?.url || ''
    } else {
      currentValue = ''
    }
  }

  let handleDownloadOriginal = async () => {
    setDownloadingImage(true)
    try {
      let key = String(currentValue)
      let extension = key.match(/\.([^./]+)$/)?.[1] || 'jpg'
      await downloadOriginalImage(
        getS3OriginalImageFilePath(key),
        `${fieldName}-${eventID}.${extension}`,
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to download original image:', error)
    } finally {
      setDownloadingImage(false)
    }
  }

  // TEMPORARY: Function to fetch Live Class thumbnail - TO BE REMOVED LATER
  let fetchLiveClassThumbnail = async () => {
    setLoadingLiveClassImage(true)
    setLiveClassImageError(null)
    setLiveClassImageName(null)

    try {
      let response = await axios
        .post('/api/pipeline/planning/events/class-image', {
          eventName: event?.name || '',
        })
        .then(pluckData)

      setLiveClassImageName(response.imageName)
      setShowLiveClassModal(true)
    } catch (error: any) {
      setLiveClassImageError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch Live Class thumbnail',
      )
      setShowLiveClassModal(true)
    } finally {
      setLoadingLiveClassImage(false)
    }
  }

  // TEMPORARY: Function to download image - TO BE REMOVED LATER
  let downloadLiveClassImage = () => {
    if (!liveClassImageName) return

    let imageUrl = `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${liveClassImageName}`
    let link = document.createElement('a')
    link.href = imageUrl
    link.download = liveClassImageName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  let form = useForm({
    defaultValues: {
      [fieldName]: currentValue,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        let updateValue = value[fieldName]
        let fieldUpdates: any = {}

        // Handle image uploads
        if (fieldType === 'image' && imageFile) {
          // Upload image using the new planning-specific endpoint
          let response = await axios.put(
            '/api/pipeline/planning/events/image',
            {
              id: eventID,
              type: fieldName === 'thumbnail' ? 'Thumbnail' : 'Banner',
            },
          )
          await axios.put(response.data, imageFile, {
            headers: {
              'Cache-Control': 'max-age=2592000',
              'content-type': 'image/jpeg',
            },
          })
          // Image upload automatically updates the event record, no additional field update needed
        }
        // Handle quiz passcode field (maps to backend quizPasscode)
        else if (fieldName === 'quizPasscode') {
          fieldUpdates.quizPasscode = updateValue
        }
        // Handle virtual location URL (maps to backend zoomURL)
        else if (fieldName === 'virtualLocationUrl') {
          fieldUpdates.zoomURL = updateValue
        }
        // Handle category - send as string (same as events form line 344-347, ignoring "Other" case)
        else if (fieldName === 'category') {
          fieldUpdates[fieldName] = updateValue
        } else {
          fieldUpdates[fieldName] = updateValue
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(
          updatePlanningEventField({
            eventId: eventID,
            fieldUpdates,
          }),
        )
        // Refetch both planning events and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchPlanningEvents())
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)
        setImageFile(undefined)

        // Close modal if in modal mode
        if (viewMode === 'modal' && onClose) {
          onClose()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update field:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let renderFieldDisabled = () => {
    if (fieldType === 'textarea') {
      return (
        <TextField
          sx={modalInputSx}
          fullWidth
          multiline
          rows={3}
          value={currentValue || ''}
          placeholder="Not set"
          disabled
        />
      )
    }

    if (fieldType === 'number') {
      return (
        <TextField
          sx={modalInputSx}
          fullWidth
          type="number"
          value={currentValue || ''}
          placeholder="Not set"
          disabled
        />
      )
    }

    if (fieldType === 'select') {
      let displayValue = ''
      if (currentValue) {
        if (typeof currentValue === 'string') {
          displayValue =
            fieldName === 'category'
              ? currentValue.replaceAll('|', '/')
              : currentValue
        } else if (typeof currentValue === 'object') {
          displayValue = JSON.stringify(currentValue)
        } else {
          displayValue = String(currentValue)
        }
      }
      return (
        <TextField
          sx={modalInputSx}
          fullWidth
          value={displayValue}
          placeholder="Not set"
          disabled
        />
      )
    }

    // Default text field
    return (
      <TextField
        sx={modalInputSx}
        fullWidth
        value={currentValue || ''}
        placeholder="Not set"
        disabled
        aria-label={fieldLabel}
        aria-describedby={
          complianceStatus ? `${fieldName}-compliance-status` : undefined
        }
      />
    )
  }

  let renderField = () => {
    if (fieldType === 'textarea') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <TextField
              sx={modalInputSx}
              fullWidth
              multiline
              rows={3}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              disabled={loading}
            />
          )}
        />
      )
    }

    if (fieldType === 'number') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <TextField
              sx={modalInputSx}
              fullWidth
              type="number"
              value={field.state.value}
              onChange={(e) => field.handleChange(Number(e.target.value))}
              onBlur={field.handleBlur}
              disabled={loading}
              slotProps={{
                htmlInput: { min: 0, step: 0.5 },
              }}
            />
          )}
        />
      )
    }

    if (fieldType === 'select') {
      let selectOptions = fieldName === 'category' ? categories : options
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <FormControl fullWidth>
              <Select
                sx={modalInputSx}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={loading}
              >
                <MenuItem value="">Unselected</MenuItem>
                {selectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {fieldName === 'category'
                      ? option.replaceAll('|', '/')
                      : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )
    }

    // Image upload field
    if (fieldType === 'image') {
      return (
        <Box sx={{ mt: 1 }}>
          <ImageUpload
            id={`${fieldName}-upload`}
            type={fieldName === 'thumbnail' ? 'thumbnail' : 'banner'}
            image={imageFile}
            setImage={setImageFile}
            existingImage={currentValue}
          />
        </Box>
      )
    }

    // Default text field
    return (
      <form.Field
        name={fieldName}
        children={(field) => (
          <TextField
            sx={modalInputSx}
            fullWidth
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={loading}
            aria-label={fieldLabel}
            aria-describedby={
              complianceStatus ? `${fieldName}-compliance-status` : undefined
            }
          />
        )}
      />
    )
  }

  let renderContent = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-label="required">
              {' '}
              *
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isApproved && (
            <Tooltip title="Locked — approved by compliance. Edit in the Compliance Dashboard.">
              <LockIcon
                fontSize="small"
                sx={{ color: 'success.main' }}
                aria-label="Field locked by compliance approval"
              />
            </Tooltip>
          )}
          {complianceStatus && (
            <Chip
              size="small"
              label={statusLabels[complianceStatus]}
              variant="outlined"
              id={`${fieldName}-compliance-status`}
              aria-label={`Compliance status: ${statusLabels[complianceStatus]}`}
              color={statusColors[complianceStatus]}
            />
          )}
        </Box>
      </Box>

      {/* TEMPORARY: Live Class thumbnail viewer button - TO BE REMOVED LATER */}
      {fieldType === 'image' &&
        (fieldName === 'thumbnail' || fieldName === 'banner') && (
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={fetchLiveClassThumbnail}
              disabled={loadingLiveClassImage || !event?.name}
              startIcon={
                loadingLiveClassImage && <CircularProgress size={16} />
              }
            >
              {loadingLiveClassImage
                ? 'Loading...'
                : 'View Live Class Thumbnail'}
            </Button>
            <Typography
              color="text.secondary"
              variant="caption"
              sx={{
                ml: 1,
              }}
            >
              (Temporary utility - will be removed)
            </Typography>
          </Box>
        )}

      {!isEditing ? (
        <Box>
          {fieldType === 'image' ? (
            <Box
              sx={{
                mb: 1,
                p: 2,
                bgcolor: 'background.paperDark',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {currentValue ? (
                <img
                  style={{
                    maxHeight: '80px',
                    maxWidth: '160px',
                    aspectRatio: fieldName === 'banner' ? '4 / 1' : '16 / 9',
                    borderRadius: '4px',
                    objectFit: 'cover',
                  }}
                  src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${currentValue?.replace('originals', 'resized')}`}
                  alt={fieldLabel}
                />
              ) : (
                <Typography
                  color="text.secondary"
                  component="span"
                  sx={{
                    fontStyle: 'italic',
                  }}
                >
                  No image uploaded
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {currentValue && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadOriginal}
                    loading={downloadingImage}
                    disabled={downloadingImage}
                    aria-label={`Download original ${fieldLabel}`}
                  >
                    Download Original
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (isApproved) {
                      setConfirmOpen(true)
                    } else {
                      setIsEditing(true)
                    }
                  }}
                  aria-label={`Edit ${fieldLabel}`}
                >
                  Upload
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {renderFieldDisabled()}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  if (isApproved) {
                    setConfirmOpen(true)
                  } else {
                    form.setFieldValue(fieldName, currentValue)
                    setIsEditing(true)
                  }
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  minWidth: 'auto',
                  px: 1,
                }}
                aria-label={`Edit ${fieldLabel}`}
              >
                Edit
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>{renderField()}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (fieldType === 'image') {
                  // For image fields, trigger the form submission which will handle image upload
                  form.handleSubmit()
                } else {
                  form.handleSubmit()
                }
              }}
              disabled={
                loading ||
                (fieldType === 'image' && !imageFile && !currentValue)
              }
              aria-label={`Save changes to ${fieldLabel}`}
            >
              {loading
                ? 'Saving...'
                : fieldType === 'image'
                  ? 'Save Image'
                  : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                setImageFile(undefined)
                if (fieldType !== 'image') {
                  form.reset()
                }
                // Close modal if in modal mode
                if (viewMode === 'modal' && onClose) {
                  onClose()
                }
              }}
              disabled={loading}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {complianceFeedback &&
        (complianceStatus === 'rejected' ||
          complianceStatus === 'unapproved') && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            role="alert"
            aria-live="polite"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
              }}
            >
              Compliance Feedback:
            </Typography>
            <Typography variant="body2">{complianceFeedback}</Typography>
          </Alert>
        )}

      {/* TEMPORARY: Live Class thumbnail viewer modal - TO BE REMOVED LATER */}
      <Dialog
        open={showLiveClassModal}
        onClose={() => {
          setShowLiveClassModal(false)
          setLiveClassImageError(null)
          setLiveClassImageName(null)
        }}
        fullWidth
        aria-labelledby="live-class-thumbnail-dialog-title"
        slotProps={{
          paper: modalPaperProps,
        }}
        maxWidth="md"
      >
        <DialogTitle id="live-class-thumbnail-dialog-title">
          Live Class Thumbnail
        </DialogTitle>
        <DialogContent>
          {liveClassImageError ? (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {liveClassImageError}
            </Alert>
          ) : liveClassImageName ? (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${liveClassImageName}`}
                alt={`Live Class Thumbnail - ${liveClassImageName}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{
                  mt: 2,
                  display: 'block',
                }}
              >
                Image: {liveClassImageName}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{ textAlign: 'center', py: 4 }}
              role="status"
              aria-live="polite"
            >
              <CircularProgress aria-label="Loading thumbnail image" />
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mt: 2,
                }}
              >
                Loading thumbnail...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setShowLiveClassModal(false)
              setLiveClassImageError(null)
              setLiveClassImageName(null)
            }}
            aria-label="Close thumbnail viewer"
          >
            Close
          </Button>
          {liveClassImageName && !liveClassImageError && (
            <Button
              variant="contained"
              onClick={downloadLiveClassImage}
              aria-label="Download thumbnail image"
            >
              Download Image
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="event-field-confirm-title"
        aria-describedby="event-field-confirm-description"
      >
        <DialogTitle id="event-field-confirm-title">
          Edit Approved Field
        </DialogTitle>
        <DialogContent id="event-field-confirm-description">
          <Typography variant="body2">
            This field has been approved by compliance. Editing will require
            re-approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )

  // If modal mode, don't render the content directly - it will be rendered inside a modal by the parent
  if (viewMode === 'modal') {
    return renderContent()
  }

  // Inline mode - render directly
  return renderContent()
}
